import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { app } from "../app"
import { languages } from "../locale"
import { translations } from "../translations"
import { buildLocalePath, getLanguageKey, getSupportedLocales } from "../helpers"
import { CheckCircleOutlined, GlobeIcon } from "./icons"

const STORAGE_KEY = "signpost-language-selected"
const PREFERRED_LANGUAGE_KEY = "preferredLanguage"
const SWITCHBOARD_COUNTRY = 42
const ORR_PROGRAMS_COUNTRY = 43

function alreadyChoseLanguage(): boolean {
  try {
    return (
      localStorage.getItem(STORAGE_KEY) === "1" ||
      Boolean(localStorage.getItem(PREFERRED_LANGUAGE_KEY))
    )
  } catch {
    return true
  }
}

function localized(text: LocalizableText, locale: string): string {
  if (!text) return ""
  return text[locale] || text[app.defaultLocale] || text["en-US"] || ""
}

export function LanguageSelectionModal() {
  const navigate = useNavigate()
  const dialogRef = useRef<HTMLDivElement>(null)

  const supportedLocales = useMemo(
    () => getSupportedLocales(),
    [app.locales, app.defaultLocale],
  )

  const [open, setOpen] = useState(
    () =>
      (app.country === SWITCHBOARD_COUNTRY || app.country === ORR_PROGRAMS_COUNTRY) &&
      supportedLocales.length > 1 &&
      !alreadyChoseLanguage(),
  )
  const [selected, setSelected] = useState(
    () => getLanguageKey(app.locale) ?? getLanguageKey(app.defaultLocale) ?? "en-US",
  )

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    dialogRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  if (!open) return null

  const isRTL = Boolean(languages[selected]?.rtl)
  const title = localized(translations.selectYourLanguage, selected)
  const description = localized(translations.selectYourLanguageDescription, selected)
  const continueLabel = localized(translations.continueLabel, selected)

  const confirm = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1")
      localStorage.setItem(PREFERRED_LANGUAGE_KEY, selected)
    } catch {
    }

    setOpen(false)
    app.locale = selected

    const updatedPath = buildLocalePath(selected, location.pathname)
    if (updatedPath !== location.pathname) {
      navigate(`${updatedPath}${location.search}${location.hash}`, { replace: true })
    }

    app.update()
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6"
      role="presentation"
    >
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-selection-title"
        tabIndex={-1}
        dir={isRTL ? "rtl" : "ltr"}
        className="relative z-[121] w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-white px-5 py-6 shadow-2xl outline-none sm:px-8 sm:py-8"
        style={{ textAlign: isRTL ? "right" : "left" }}
      >
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e8eff7] text-[#1b3a8c]">
          <GlobeIcon aria-hidden="true" style={{ fontSize: "1.75rem" }} />
        </span>

        <h2
          id="language-selection-title"
          className="mt-5 mb-0 text-3xl font-extrabold leading-tight text-[#12276b]"
        >
          {title}
        </h2>
        <p className="mt-2 mb-0 text-base leading-6 text-[#5b6672]">{description}</p>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {supportedLocales.map((localeKey) => {
            const language = languages[localeKey]
            const isSelected = localeKey === selected
            return (
              <button
                key={localeKey}
                type="button"
                onClick={() => setSelected(localeKey)}
                aria-pressed={isSelected}
                className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl border-2 border-solid bg-white px-4 py-3 transition-colors ${
                  isSelected
                    ? "border-[#2563a8] bg-[#eef4fb]"
                    : "border-[#e3e7ec] hover:border-[#c3d3e6]"
                }`}
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                <span className="min-w-0">
                  <span
                    className="block truncate text-base font-bold text-[#12276b]"
                    style={{ unicodeBidi: "isolate" }}
                  >
                    {language?.name ?? localeKey}
                  </span>
                  {language?.english && (
                    <span className="block truncate text-sm text-[#5b6672]">
                      {language.english}
                    </span>
                  )}
                </span>
                {isSelected && (
                  <CheckCircleOutlined
                    aria-hidden="true"
                    className="flex-shrink-0 text-[#2563a8]"
                    style={{ fontSize: "1.25rem" }}
                  />
                )}
              </button>
            )
          })}
        </div>

        <button
          type="button"
          onClick={confirm}
          className="mt-7 h-12 w-full cursor-pointer rounded-full border-none bg-[#EC5C2E] text-center text-base font-semibold text-white transition-colors hover:bg-[#d04a22]"
          style={{ textAlign: "center" }}
        >
          {continueLabel}
        </button>
      </div>
    </div>
  )
}
